import { z } from 'zod';
import { BULK_ACTION_MAX_LEADS } from '../constants';
import { normalizeName } from '../funnel/normalize';

// ---------------------------------------------------------------- etiquetas

/** Colores disponibles para las etiquetas (la web los traduce a su paleta). */
export const TAG_COLORS = [
  'gris',
  'rojo',
  'naranja',
  'ambar',
  'verde',
  'turquesa',
  'azul',
  'indigo',
  'morado',
  'rosa',
] as const;
export type TagColor = (typeof TAG_COLORS)[number];
export const TAG_COLOR_LABELS: Record<TagColor, string> = {
  gris: 'Gris',
  rojo: 'Rojo',
  naranja: 'Naranja',
  ambar: 'Ámbar',
  verde: 'Verde',
  turquesa: 'Turquesa',
  azul: 'Azul',
  indigo: 'Índigo',
  morado: 'Morado',
  rosa: 'Rosa',
};

export const tagInputSchema = z.object({
  /** Al editar; sin id se crea. */
  id: z.string().uuid().optional(),
  companyId: z.string().uuid(),
  name: z.string().trim().min(1, 'Escribe el nombre').max(40),
  color: z.enum(TAG_COLORS).default('gris'),
  active: z.boolean().default(true),
  sortOrder: z.coerce.number().int().min(0).default(0),
});
export type TagInput = z.infer<typeof tagInputSchema>;
export type TagPayload = z.input<typeof tagInputSchema>;

export interface TagDto {
  id: string;
  name: string;
  color: TagColor;
  active: boolean;
  sortOrder: number;
}
/** Con cuántos leads se usa (solo en administración). */
export interface TagAdminDto extends TagDto {
  leads: number;
}
/** Etiqueta tal como viaja con cada lead. */
export interface LeadTagDto {
  id: string;
  name: string;
  color: TagColor;
}

/** Reemplaza el conjunto de etiquetas de un lead. */
export const setLeadTagsSchema = z.object({
  tagIds: z.array(z.string().uuid()).max(50),
});
export type SetLeadTagsInput = z.infer<typeof setLeadTagsSchema>;

/** Etiquetar o quitar etiquetas a varios leads desde la tabla. */
export const bulkTagSchema = z
  .object({
    leadIds: z.array(z.string().uuid()).min(1).max(BULK_ACTION_MAX_LEADS),
    add: z.array(z.string().uuid()).max(50).default([]),
    remove: z.array(z.string().uuid()).max(50).default([]),
  })
  .refine((v) => v.add.length + v.remove.length > 0, 'Elige al menos una etiqueta');
export type BulkTagInput = z.infer<typeof bulkTagSchema>;
export type BulkTagPayload = z.input<typeof bulkTagSchema>;

// ---------------------------------------------------------------- campos personalizados

/** Cuántos campos adicionales activos puede definir cada empresa. */
export const MAX_CUSTOM_FIELDS = 5;

export const CUSTOM_FIELD_TYPES = ['TEXTO', 'NUMERO', 'FECHA', 'LISTA', 'SI_NO'] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];
export const CUSTOM_FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  TEXTO: 'Texto',
  NUMERO: 'Número',
  FECHA: 'Fecha',
  LISTA: 'Lista de opciones',
  SI_NO: 'Sí / No',
};

/** Clave estable con la que se guarda el valor (`leads.custom`) y se mapea en importaciones y webhooks. */
export const CUSTOM_FIELD_KEY = /^[a-z][a-z0-9_]{0,39}$/;

/** "Nº de hijos" → "n_de_hijos". */
export function customFieldKey(label: string): string {
  const base = normalizeName(label)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40)
    .replace(/_+$/g, '');
  const key = /^[a-z]/.test(base) ? base : `campo_${base}`.slice(0, 40).replace(/_+$/g, '');
  return key || 'campo';
}

export const customFieldInputSchema = z
  .object({
    id: z.string().uuid().optional(),
    companyId: z.string().uuid(),
    /** Si falta se deriva de la etiqueta. */
    key: z
      .string()
      .trim()
      .regex(CUSTOM_FIELD_KEY, 'Solo minúsculas, números y guion bajo; empieza con letra')
      .optional(),
    label: z.string().trim().min(1, 'Escribe el nombre del campo').max(60),
    type: z.enum(CUSTOM_FIELD_TYPES),
    options: z.array(z.string().trim().min(1).max(60)).max(30).default([]),
    required: z.boolean().default(false),
    active: z.boolean().default(true),
    sortOrder: z.coerce.number().int().min(0).default(0),
  })
  .superRefine((v, ctx) => {
    if (v.type === 'LISTA') {
      const unique = new Set(v.options.map((o) => normalizeName(o)));
      if (v.options.length === 0)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['options'],
          message: 'Una lista necesita al menos una opción',
        });
      else if (unique.size !== v.options.length)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['options'],
          message: 'Hay opciones repetidas',
        });
    }
  });
export type CustomFieldInput = z.infer<typeof customFieldInputSchema>;
export type CustomFieldPayload = z.input<typeof customFieldInputSchema>;

export interface CustomFieldDto {
  id: string;
  key: string;
  label: string;
  type: CustomFieldType;
  options: string[];
  /** Obligatorio en los formularios de la web (la API y las integraciones nunca se bloquean por esto). */
  required: boolean;
  active: boolean;
  sortOrder: number;
}

export type CustomValue = string | number | boolean | null;
export type CustomValues = Record<string, CustomValue>;

/** Valores de campos personalizados tal como llegan (se validan contra la definición de la empresa). */
export const customValuesInputSchema = z.record(z.string(), z.unknown());

const TRUE_WORDS = new Set(['si', 'sí', 'yes', 'true', '1', 'x', 'verdadero']);
const FALSE_WORDS = new Set(['no', 'false', '0', 'falso']);

export interface ParseNumberOptions {
  /**
   * Separador decimal que usa el archivo cuando una cifra es ambigua («1,250» o «1.250»: ¿mil doscientos
   * cincuenta o uno coma veinticinco?). Un CSV con `;` como separador de columnas suele venir con coma decimal;
   * las celdas numéricas de un Excel llegan siempre con punto. Sin pista, un solo separador seguido de exactamente
   * tres dígitos se lee como miles («1,250» = 1250).
   */
  decimal?: ',' | '.';
}

/**
 * Más largo que esto no es una cifra. Se descarta **antes** de aplicar cualquier expresión regular: con una tirada
 * larga de espacios («1» + 20 000 espacios + «x») el sufijo `\s*(…)$` retrocedía de forma cuadrática y congelaba
 * la API casi un segundo por celda.
 */
export const MAX_NUMBER_TEXT_LENGTH = 40;

/** Monedas y unidades que acompañan a una cifra en las listas de precios: «S/ 350,000», «US$ 152,000.00», «45.5 m2». */
const NUMBER_PREFIX = /^(s\/\.?|us\$|usd|pen|\$|€)\s*/i;
const NUMBER_SUFFIX = /\s*(m2|m²|m\^2|mt2|usd|pen|soles|d[oó]lares|\$|%)$/i;

/** «S/», «S/.», «PEN», «soles» → PEN; «US$», «$», «USD», «dólares» → USD; «m2», «%», «€» no dicen moneda. */
function currencyOfToken(token: string | undefined): 'PEN' | 'USD' | null {
  if (!token) return null;
  const t = token.toLowerCase();
  if (t.startsWith('s/') || t === 'pen' || t === 'soles') return 'PEN';
  if (t === 'us$' || t === '$' || t === 'usd' || t.startsWith('d')) return 'USD';
  return null;
}

/** Lo que trae una celda con cifra: el número (null si no es un número) y la moneda, si la celda la dice. */
export interface ParsedAmount {
  value: number | null;
  /** Moneda escrita como prefijo o sufijo («S/ 350,000», «1,500 USD»); null si la celda no la dice. */
  currency: 'PEN' | 'USD' | null;
}

/**
 * Lector de cifras con moneda: además del número, devuelve la moneda que la celda trae escrita (prefijo o sufijo),
 * para que una lista de precios en soles no se guarde en dólares por omisión. Prefijo y sufijo con monedas
 * distintas («US$ 100 soles») no es un número. Ver `parseNumber` para las reglas de miles y decimales.
 */
export function parseMoney(v: unknown, opts: ParseNumberOptions = {}): ParsedAmount {
  const none: ParsedAmount = { value: null, currency: null };
  if (typeof v === 'number') return { value: Number.isFinite(v) ? v : null, currency: null };
  if (typeof v !== 'string') return none;
  const trimmed = v.trim();
  if (!trimmed || trimmed.length > MAX_NUMBER_TEXT_LENGTH) return none;
  const prefix = NUMBER_PREFIX.exec(trimmed);
  let s = prefix ? trimmed.slice(prefix[0].length) : trimmed;
  const suffix = NUMBER_SUFFIX.exec(s);
  if (suffix) s = s.slice(0, suffix.index);
  const prefixCurrency = currencyOfToken(prefix?.[1]);
  const suffixCurrency = currencyOfToken(suffix?.[1]);
  if (prefixCurrency && suffixCurrency && prefixCurrency !== suffixCurrency) return none;
  const currency = prefixCurrency ?? suffixCurrency;
  s = s.replace(/\s+/g, '');
  if (!s) return none;
  const commas = (s.match(/,/g) ?? []).length;
  const dots = (s.match(/\./g) ?? []).length;
  if (commas && dots) {
    // Los dos separadores: el último es el decimal y el otro agrupa miles («1.250.000,50», «350,000.00»).
    const decimal = s.lastIndexOf(',') > s.lastIndexOf('.') ? ',' : '.';
    const thousands = decimal === ',' ? '.' : ',';
    if (!new RegExp(`^-?\\d{1,3}(\\${thousands}\\d{3})+\\${decimal}\\d+$`).test(s)) return none;
    s = s.split(thousands).join('').replace(decimal, '.');
  } else if (commas || dots) {
    const sep = commas ? ',' : '.';
    const count = commas || dots;
    const groups = /^-?\d{1,3}(?:[.,]\d{3})+$/.test(s);
    if (count > 1) {
      // Varios separadores iguales solo pueden ser miles («1.250.000», «1,250,000»).
      if (!groups) return none;
      s = s.split(sep).join('');
    } else if (groups) {
      // Un separador con exactamente tres dígitos detrás: miles, salvo que el archivo use ese signo como decimal.
      s = opts.decimal === sep ? s.replace(sep, '.') : s.replace(sep, '');
    } else {
      // Decimal: «45,5», «350000,00», «85.5».
      s = s.replace(sep, '.');
    }
  }
  if (!/^-?\d+(\.\d+)?$/.test(s)) return none;
  const n = Number(s);
  return Number.isFinite(n) ? { value: n, currency } : none;
}

/**
 * Único lector de cifras del CRM (campos personalizados, carga de unidades): entiende «S/», «US$», «USD», miles
 * con punto o coma y coma decimal. «350000,00» = 350000; «45,5» = 45.5; «1.250.000» = 1250000;
 * «S/. 350,000.00» = 350000; «45.5 m2» = 45.5; «1,250.50» = 1250.5; «1.250,50» = 1250.5. Devuelve `null`
 * cuando el texto **no es un número** («consultar», «n.d.», «S/C», «-», vacío, más de `MAX_NUMBER_TEXT_LENGTH`
 * caracteres): quien llama decide si eso es un error o un «sin dato». El 0 explícito sí es un número.
 */
export function parseNumber(v: unknown, opts: ParseNumberOptions = {}): number | null {
  return parseMoney(v, opts).value;
}

/** Acepta Date, ISO, YYYY-MM-DD y DD/MM/YYYY; devuelve YYYY-MM-DD. */
function parseDate(v: unknown): string | null {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.toISOString().slice(0, 10);
  if (typeof v !== 'string') return null;
  const s = v.trim();
  let m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/.exec(s);
  if (m) return valid(m[1]!, m[2]!, m[3]!);
  m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s);
  if (m) return valid(m[3]!, m[2]!.padStart(2, '0'), m[1]!.padStart(2, '0'));
  return null;
}
function valid(y: string, mo: string, d: string): string | null {
  const iso = `${y}-${mo}-${d}`;
  const date = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== iso ? null : iso;
}

export interface CustomValuesResult {
  /** Solo las claves válidas, con el valor normalizado (null = borrar). */
  values: CustomValues;
  errors: Array<{ key: string; message: string }>;
}

/**
 * Valida y normaliza los valores contra los campos de la empresa. Nunca lanza: quien llama decide si los errores
 * bloquean (API y formularios) o si solo se descartan (importaciones y webhooks, que no deben perder el lead).
 */
export function validateCustomValues(
  fields: ReadonlyArray<Pick<CustomFieldDto, 'key' | 'label' | 'type' | 'options' | 'active'>>,
  input: Record<string, unknown>,
): CustomValuesResult {
  const values: CustomValues = {};
  const errors: CustomValuesResult['errors'] = [];
  const byKey = new Map(fields.filter((f) => f.active).map((f) => [f.key, f]));
  for (const [key, raw] of Object.entries(input)) {
    const field = byKey.get(key);
    if (!field) {
      errors.push({ key, message: `Campo desconocido: ${key}` });
      continue;
    }
    if (raw === null || raw === undefined || (typeof raw === 'string' && raw.trim() === '')) {
      values[key] = null;
      continue;
    }
    switch (field.type) {
      case 'TEXTO': {
        const s = String(raw).trim();
        if (s.length > 300) errors.push({ key, message: `${field.label}: máximo 300 caracteres` });
        else values[key] = s;
        break;
      }
      case 'NUMERO': {
        const n = parseNumber(raw);
        if (n === null) errors.push({ key, message: `${field.label}: debe ser un número` });
        else values[key] = n;
        break;
      }
      case 'FECHA': {
        const d = parseDate(raw);
        if (!d) errors.push({ key, message: `${field.label}: fecha no válida (usa AAAA-MM-DD)` });
        else values[key] = d;
        break;
      }
      case 'LISTA': {
        const wanted = normalizeName(String(raw));
        const option = field.options.find((o) => normalizeName(o) === wanted);
        if (!option)
          errors.push({
            key,
            message: `${field.label}: elige una opción (${field.options.join(', ')})`,
          });
        else values[key] = option;
        break;
      }
      case 'SI_NO': {
        if (typeof raw === 'boolean') values[key] = raw;
        else {
          const w = String(raw).trim().toLowerCase();
          if (TRUE_WORDS.has(w)) values[key] = true;
          else if (FALSE_WORDS.has(w)) values[key] = false;
          else errors.push({ key, message: `${field.label}: indica Sí o No` });
        }
        break;
      }
    }
  }
  return { values, errors };
}

/** Une los valores nuevos con los existentes; un null borra la clave. */
export function mergeCustomValues(current: unknown, patch: CustomValues): CustomValues {
  const base: CustomValues =
    current && typeof current === 'object' && !Array.isArray(current)
      ? { ...(current as CustomValues) }
      : {};
  for (const [k, v] of Object.entries(patch)) {
    if (v === null) delete base[k];
    else base[k] = v;
  }
  return base;
}

/** Texto para mostrar un valor (fechas y Sí / No en español; el resto tal cual). */
export function formatCustomValue(field: Pick<CustomFieldDto, 'type'>, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (field.type === 'SI_NO') return value === true || value === 'true' ? 'Sí' : 'No';
  if (field.type === 'FECHA' && typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  }
  if (field.type === 'NUMERO' && typeof value === 'number')
    return new Intl.NumberFormat('es-PE', { maximumFractionDigits: 2 }).format(value);
  return String(value);
}

/** Prefijo con el que un campo personalizado se mapea en importaciones y webhooks: `custom.<clave>`. */
export const CUSTOM_MAPPING_PREFIX = 'custom.';
export function customMappingKey(key: string): string {
  return `${CUSTOM_MAPPING_PREFIX}${key}`;
}
export function isCustomMappingKey(k: string): boolean {
  return (
    k.startsWith(CUSTOM_MAPPING_PREFIX) &&
    CUSTOM_FIELD_KEY.test(k.slice(CUSTOM_MAPPING_PREFIX.length))
  );
}
